import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { Accordion, AccordionTab } from 'primereact/accordion'
import PageHeader from '../../components/Shared/PageHeader'
import TableLoading from '../../components/Shared/TableLoading'
import NoDataFound from '../../components/Shared/NoDataFound'
import { getJournalEntryAuditLogs } from '../../store/slices/TransactionsSlice'
import {
  MdAccessTime,
  MdCompareArrows,
  MdHistory,
  MdPersonOutline
} from 'react-icons/md'

const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const formattedHours = String(hours % 12 || 12).padStart(2, '0')
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`
}

const parseSnapshot = (value, fallback) => {
  if (value && typeof value === 'object') return value
  if (!value || typeof value !== 'string') return fallback || null

  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback || null
  }
}

const formatAmount = value => Number(value || 0).toFixed(2)

const getActionLabel = (action, t) => {
  const labels = {
    create: t('audit_action_create'),
    upsert_update: t('audit_action_upsert_update'),
    upsert_replace: t('audit_action_upsert_replace')
  }
  return labels[action] || action || ''
}

const AuditSnapshot = ({ title, snapshot, t, lang }) => {
  const journalEntry = snapshot?.journal_entry || {}
  const lines = snapshot?.lines || []

  return (
    <div className='border border-gray-200 rounded-md overflow-hidden bg-white'>
      <div className='bg-gray-50 px-3 py-2 flex flex-wrap items-center justify-between gap-2'>
        <span className='font-semibold text-gray-800'>{title}</span>
        <span className='text-xs text-gray-500'>
          {lines.length} {t('lines')}
        </span>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3 p-3 text-sm'>
        <div>
          <span className='block text-gray-500'>{t('total_debit')}</span>
          <span className='font-semibold'>
            {formatAmount(journalEntry?.total_debit)} {t('SAR')}
          </span>
        </div>
        <div>
          <span className='block text-gray-500'>{t('total_credit')}</span>
          <span className='font-semibold'>
            {formatAmount(journalEntry?.total_credit)} {t('SAR')}
          </span>
        </div>
        <div>
          <span className='block text-gray-500'>{t('description')}</span>
          <span className='font-semibold'>
            {journalEntry?.description || '-'}
          </span>
        </div>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead className='bg-gray-50 text-gray-600'>
            <tr>
              <th className='text-start px-3 py-2 font-semibold'>
                {t('Account Code')}
              </th>
              <th className='text-start px-3 py-2 font-semibold'>
                {t('AccountName')}
              </th>
              <th className='text-start px-3 py-2 font-semibold'>
                {t('type')}
              </th>
              <th className='text-end px-3 py-2 font-semibold'>
                {t('amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.length ? (
              lines.map((line, index) => (
                <tr
                  key={line?.id || index}
                  className='border-t border-gray-100'
                >
                  <td className='px-3 py-2'>{line?.account_code || '-'}</td>
                  <td className='px-3 py-2'>
                    {lang === 'ar'
                      ? line?.account_name_ar || line?.account_name_en || '-'
                      : line?.account_name_en || line?.account_name_ar || '-'}
                  </td>
                  <td className='px-3 py-2'>{line?.entry_type || '-'}</td>
                  <td className='px-3 py-2 text-end'>
                    {formatAmount(line?.amount)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className='px-3 py-4 text-center text-gray-500' colSpan='4'>
                  {t('NoData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const JournalAuditLogsPage = () => {
  const { journalEntryNumber } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const lang = i18next.language
  const { auditLogs, auditLogsLoading } = useSelector(
    state => state.transaction
  )
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (journalEntryNumber) {
      dispatch(getJournalEntryAuditLogs(journalEntryNumber))
    }
  }, [dispatch, journalEntryNumber])

  useEffect(() => {
    setActiveIndex(auditLogs?.length ? 0 : null)
  }, [auditLogs])

  return (
    <div className='flex rounded-md w-full gap-2 flex-col icon_hover'>
      <PageHeader
        title={`${t('audit_logs')} of ${t('entry_number')} #${
          journalEntryNumber || '-'
        }`}
        onBack={() => navigate(-1)}
      />
      {/* <div className="flex justify-end mb-2 text-sm">
        <div className="text-gray-500 flex items-center gap-1">
          <MdHistory size={18} />
          {auditLogs?.length || 0} {t("audit_logs")}
        </div>
      </div> */}
      <div className='bg-white rounded-md'>
        {auditLogsLoading ? (
          <TableLoading />
        ) : auditLogs?.length ? (
          <Accordion
            activeIndex={activeIndex}
            onTabChange={e => setActiveIndex(e.index)}
          >
            {auditLogs.map((log, index) => {
              const oldSnapshot = parseSnapshot(
                log?.old_values_json,
                parseSnapshot(log?.old_values)
              )
              const newSnapshot = parseSnapshot(
                log?.new_values_json,
                parseSnapshot(log?.new_values)
              )
              const oldTotal = oldSnapshot?.journal_entry?.total_debit
              const newTotal = newSnapshot?.journal_entry?.total_debit

              return (
                <AccordionTab
                  key={log?.id}
                  header={
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                      <div className='flex items-start gap-3'>
                        {/* <span className='mt-1 flex-shrink-0 p-1.5 bg-gray-100 rounded-lg text-white'>
                          <MdHistory size={20} color='#0070C2' />
                        </span> */}
                        <div>
                          <div className='text-base font-semibold text-gray-700 capitalize flex flex-wrap items-center gap-2'>
                            {getActionLabel(log?.action, t)}
                            {/* {log?.source ? (
                              <span className='text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded'>
                                {log.source}
                              </span>
                            ) : null} */}
                          </div>
                          <div className='text-sm text-gray-600 flex flex-wrap items-center gap-x-3 gap-y-1'>
                            <span className='inline-flex items-center gap-1'>
                              <MdPersonOutline size={16} />
                              {log?.action_by_user_name ||
                                log?.api_user_name ||
                                '-'}
                            </span>
                            <span className='inline-flex items-center gap-1'>
                              <MdAccessTime size={16} />
                              {formatDate(log?.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className='text-sm text-gray-700 inline-flex items-center gap-2'>
                        <MdCompareArrows size={18} />
                        {formatAmount(oldTotal)} -&gt; {formatAmount(newTotal)}{' '}
                        {t('SAR')}
                      </span>
                    </div>
                  }
                  pt={{
                    header: {
                      className: `${
                        activeIndex === index
                          ? 'bg-gray-200 mt-4'
                          : 'bg-white mt-4'
                      }`
                    },
                    headeraction: {
                      className: 'shadow-none focus:shadow-none bg-transparent'
                    }
                  }}
                >
                  <div>
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
                      <AuditSnapshot
                        title={t('old_values')}
                        snapshot={oldSnapshot}
                        t={t}
                        lang={lang}
                      />
                      <AuditSnapshot
                        title={t('new_values')}
                        snapshot={newSnapshot}
                        t={t}
                        lang={lang}
                      />
                    </div>
                  </div>
                </AccordionTab>
              )
            })}
          </Accordion>
        ) : (
          <NoDataFound title={t('NoData')} />
        )}
      </div>
    </div>
  )
}

export default JournalAuditLogsPage
